import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Razorpay hosted Payment Page / Payment Link. Checkout, receipts and the
// record of every payment live in the Razorpay Dashboard; we only link out to
// it. Prefill params below carry the signer's identity onto that record so it
// can be matched back to the Formspree lead by email.
// TODO: replace with the real hosted URL from the Razorpay Dashboard.
const RAZORPAY_URL = 'https://pages.razorpay.com/REPLACE_ME';
const APP_URL = 'https://www.healthymitra.com';

interface ContactFormProps {
  isOpen: boolean;
  onClose: () => void;
  formType: 'partnership' | 'contact' | 'pilot' | 'clinical' | 'research' | 'investment' | 'waitlist';
}

export const ContactForm: React.FC<ContactFormProps> = ({ isOpen, onClose, formType }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Captured from the form before it is reset, so the post-submit "Pay" link
  // can prefill the Razorpay page.
  const [submittedName, setSubmittedName] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [submittedContact, setSubmittedContact] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://formspree.io/f/mkoykpdr', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        const first = (formData.get('firstName') as string) || '';
        const last = (formData.get('lastName') as string) || '';
        setSubmittedName([first, last].filter(Boolean).join(' ').trim());
        setSubmittedEmail((formData.get('email') as string) || '');
        setSubmittedContact((formData.get('contact') as string) || '');
        setIsSubmitted(true);
        form.reset();
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const razorpayHref = () => {
    const params = new URLSearchParams();
    if (submittedName) params.set('prefill[name]', submittedName);
    if (submittedEmail) params.set('prefill[email]', submittedEmail);
    if (submittedContact) params.set('prefill[contact]', submittedContact);
    params.set('notes[source]', 'early-access-landing');
    return `${RAZORPAY_URL}?${params.toString()}`;
  };

  if (!isOpen) return null;

  const getFormDetails = () => {
    switch (formType) {
      case 'pilot':
        return {
          title: 'Pilot Partnership',
          subtitle: 'Let\'s launch a pilot implementation of HealthyMitra at your healthcare organization',
          subject: 'Pilot Partnership Inquiry'
        };
      case 'clinical':
        return {
          title: 'Clinical Collaboration',
          subtitle: 'Partner with us on clinical research and validation studies',
          subject: 'Clinical Collaboration Inquiry'
        };
      case 'research':
        return {
          title: 'Research Collaboration',
          subtitle: 'Collaborate on academic research in healthcare continuity and AI-assisted care',
          subject: 'Research Collaboration Inquiry'
        };
      case 'investment':
        return {
          title: 'Strategic Investment',
          subtitle: 'Join us in building the future of healthcare continuity',
          subject: 'Investment Inquiry'
        };
      case 'waitlist':
        return {
          title: 'Join the Waitlist',
          subtitle: 'Be among the first to experience HealthyMitra and help us prioritize access',
          subject: 'Waitlist Registration'
        };
      case 'partnership':
        return {
          title: 'Partnership',
          subtitle: 'Let\'s explore how HealthyMitra can transform your healthcare delivery',
          subject: 'Partnership Inquiry'
        };
      default:
        return {
          title: 'Contact Us',
          subtitle: 'Get in touch with our team',
          subject: 'General Inquiry'
        };
    }
  };

  const formDetails = getFormDetails();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-soft max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-deep-slate">{formDetails.title}</h2>
            <button
              onClick={onClose}
              className="text-warm-gray hover:text-soft-black transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {isSubmitted ? (
            <div className="text-center py-8">
              <div className="text-forest-green mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-deep-slate mb-2">Thank you!</h3>
              <p className="text-warm-gray">We'll get back to you soon.</p>

              {formType === 'waitlist' && (
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <p className="text-deep-slate font-medium mb-1">
                    Lock in your early-access discount
                  </p>
                  <p className="text-sm text-warm-gray mb-5">
                    1,500 credits &middot; about 30% off &middot; secure checkout by Razorpay
                  </p>
                  <a
                    href={razorpayHref()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block minimal-button px-6 py-3 bg-muted-teal text-white hover:bg-muted-teal/90"
                  >
                    Pay &#8377;999 &amp; secure early access
                  </a>
                  <p className="mt-3 text-xs leading-relaxed text-warm-gray">
                    Your purchase is subject to our{' '}
                    <a className="underline hover:text-deep-slate" href={`${APP_URL}/terms`}>Terms</a>
                    {' '}and{' '}
                    <a className="underline hover:text-deep-slate" href={`${APP_URL}/payments-refunds`}>Payments &amp; Refunds Policy</a>.
                  </p>
                </div>
              )}

              <button
                onClick={onClose}
                className="mt-6 minimal-button px-6 py-2 text-sm"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <p className="text-warm-gray mb-6">{formDetails.subtitle}</p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="hidden" 
                  name="_subject" 
                  value={`HealthyMitra ${formDetails.subject}`}
                />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-deep-slate mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-muted-teal/20 focus:border-muted-teal transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-deep-slate mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-muted-teal/20 focus:border-muted-teal transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-deep-slate mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-muted-teal/20 focus:border-muted-teal transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-deep-slate mb-2">
                    Organization
                  </label>
                  <input
                    type="text"
                    name="organization"
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-muted-teal/20 focus:border-muted-teal transition-colors"
                  />
                </div>

                {formType === 'waitlist' && (
                  <>
                    {/* Tags every early-access lead in Formspree so payment-intent
                        signups can be filtered and reconciled against Razorpay. */}
                    <input type="hidden" name="paymentIntent" value="early-access-999" />

                    <div>
                      <label className="block text-sm font-medium text-deep-slate mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="contact"
                        placeholder="For your payment receipt and account setup"
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-muted-teal/20 focus:border-muted-teal transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-deep-slate mb-2">
                        User Type *
                      </label>
                      <select
                        name="userType"
                        required
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-muted-teal/20 focus:border-muted-teal transition-colors"
                      >
                        <option value="">Select your role</option>
                        <option value="patient">Patient</option>
                        <option value="caregiver">Caregiver</option>
                        <option value="clinician">Clinician</option>
                        <option value="business">Business/Organization</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-deep-slate mb-2">
                        Financial Commitment (USD) *
                      </label>
                      <input
                        type="number"
                        name="financialBid"
                        required
                        min="0"
                        step="1"
                        placeholder="How much are you willing to invest for early access?"
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-muted-teal/20 focus:border-muted-teal transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-deep-slate mb-2">
                        Desired Timeline *
                      </label>
                      <select
                        name="timeline"
                        required
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-muted-teal/20 focus:border-muted-teal transition-colors"
                      >
                        <option value="">When do you need access?</option>
                        <option value="immediate">Immediately (within 1 month)</option>
                        <option value="3months">Within 3 months</option>
                        <option value="6months">Within 6 months</option>
                        <option value="1year">Within 1 year</option>
                        <option value="flexible">I'm flexible</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-deep-slate mb-2">
                        Primary Use Case *
                      </label>
                      <textarea
                        name="useCase"
                        required
                        rows={3}
                        placeholder="Describe your specific healthcare continuity challenge and how you plan to use HealthyMitra..."
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-muted-teal/20 focus:border-muted-teal transition-colors resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-deep-slate mb-2">
                        Commitment Pledge *
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input type="checkbox" name="commitments" value="feedback" className="mr-2" />
                          <span className="text-sm text-deep-slate">Provide detailed product feedback</span>
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" name="commitments" value="studies" className="mr-2" />
                          <span className="text-sm text-deep-slate">Participate in clinical studies</span>
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" name="commitments" value="testimonials" className="mr-2" />
                          <span className="text-sm text-deep-slate">Provide testimonials and case studies</span>
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" name="commitments" value="referrals" className="mr-2" />
                          <span className="text-sm text-deep-slate">Refer other potential users</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-deep-slate mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        name="location"
                        placeholder="City, State/Region, Country"
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-muted-teal/20 focus:border-muted-teal transition-colors"
                      />
                    </div>
                  </>
                )}

                {['partnership', 'pilot', 'clinical', 'research', 'investment'].includes(formType) && (
                  <div>
                    <label className="block text-sm font-medium text-deep-slate mb-2">
                      Partnership Type
                    </label>
                    <select
                      name="partnershipType"
                      defaultValue={formType === 'partnership' ? '' : formType}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-muted-teal/20 focus:border-muted-teal transition-colors"
                    >
                      <option value="">Select partnership type</option>
                      <option value="pilot">Pilot Partnership</option>
                      <option value="clinical">Clinical Collaboration</option>
                      <option value="research">Research Collaboration</option>
                      <option value="investment">Strategic Investment</option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-deep-slate mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    required={formType !== 'waitlist'}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-muted-teal/20 focus:border-muted-teal transition-colors resize-none"
                    placeholder={['partnership', 'pilot', 'clinical', 'research', 'investment'].includes(formType)
                      ? 'Tell us about your organization and how you\'d like to partner with HealthyMitra...'
                      : formType === 'waitlist' 
                      ? 'Any additional information that might help us prioritize your access (medical urgency, specific conditions, etc.)...'
                      : 'How can we help you?'
                    }
                  />
                </div>

                <p className="text-xs leading-relaxed text-warm-gray">
                  By submitting, you acknowledge that HealthyMitra will process
                  your information to respond to your request as described in
                  our{' '}
                  <a
                    className="underline hover:text-deep-slate"
                    href={`${APP_URL}/privacy`}
                  >
                    Privacy Policy
                  </a>.
                </p>

                <div className="flex space-x-4 pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 minimal-button py-3 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : formType === 'waitlist' ? 'Join Waitlist' : `Send ${['partnership', 'pilot', 'clinical', 'research', 'investment'].includes(formType) ? 'Partnership Request' : 'Message'}`}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-6 py-3 text-warm-gray hover:text-soft-black transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};
